import { CONNECTOR_FIELDS, type IntegrationKind } from '@developerz/domain';
import { createEffect, createMemo, createSignal, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { ConnectInput } from '../lib/integrations';
import { Field } from './Field';
import { Modal } from './Modal';
import { Button, buttonClass } from './ui/Button';

// Connect form for one connector (spec §3.5 `--lg` form dialog). Fields are
// driven by the domain's `CONNECTOR_FIELDS` descriptor (single source), so
// adding a connector needs no change here. Secret fields render as password
// inputs and only live in this local until POSTed and sealed server-side. The
// parent owns the mutation + busy/error state; `kind === null` keeps it closed.
// The footer band sits INSIDE the form (submit button) and the form is the
// last template child (happy-dom #481).

export type ConnectIntegrationModalProps = {
  readonly kind: IntegrationKind | null;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onSubmit: (input: ConnectInput) => void;
  readonly onClose: () => void;
};

export function ConnectIntegrationModal(props: ConnectIntegrationModalProps): JSX.Element {
  const [values, setValues] = createSignal<Record<string, string>>({});
  const fields = createMemo(() => (props.kind ? CONNECTOR_FIELDS[props.kind] : []));

  // Reset the form whenever the target connector changes (open a fresh one, or
  // reopen after a submit) so no value bleeds across connectors.
  createEffect(() => {
    props.kind;
    setValues({});
  });

  const incomplete = (): boolean =>
    fields().some((f) => f.required && (values()[f.name] ?? '').trim() === '');

  const submit = (e: Event): void => {
    e.preventDefault();
    const kind = props.kind;
    if (!kind || incomplete()) return;
    const config: Record<string, string> = {};
    const secret: Record<string, string> = {};
    for (const f of fields()) {
      const v = (values()[f.name] ?? '').trim();
      if (v === '') continue; // skip empty optionals — the server applies defaults
      (f.group === 'secret' ? secret : config)[f.name] = v;
    }
    props.onSubmit({ kind, config, secret });
  };

  return (
    <Modal
      open={props.kind !== null}
      size="lg"
      title={props.kind ? t(`integrations.kind.${props.kind}`) : ''}
      onClose={props.onClose}
    >
      <form class="integration-form" onSubmit={submit}>
        <div class="field-group">
          <For each={fields()}>
            {(field) => (
              <Field
                label={
                  field.required
                    ? t(`integrations.field.${field.name}`)
                    : `${t(`integrations.field.${field.name}`)} ${t('integrations.optional')}`
                }
                id={`connect-${field.name}`}
              >
                {(a11y) => (
                  <input
                    {...a11y}
                    class="input"
                    type={field.secret ? 'password' : 'text'}
                    value={values()[field.name] ?? ''}
                    onInput={(e) =>
                      setValues((prev) => ({ ...prev, [field.name]: e.currentTarget.value }))
                    }
                    autocomplete={field.secret ? 'off' : undefined}
                    required={field.required}
                  />
                )}
              </Field>
            )}
          </For>
        </div>
        <Show when={props.error}>
          <p class="field__error" role="alert">
            {props.error}
          </p>
        </Show>
        <div class="dialog__footer">
          <Button variant="outline" onClick={props.onClose}>
            {t('integrations.cancel')}
          </Button>
          <button type="submit" class={buttonClass()} disabled={props.busy || incomplete()}>
            {props.busy ? t('integrations.connecting') : t('integrations.connect_submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
